import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Paper, Typography, Chip, IconButton, Tooltip } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Add, Receipt } from '@mui/icons-material';
import { salesService } from '../../services/sales.service';
import { useLanguage } from '../../i18n/LanguageContext';
import { SalesOrder } from '../../types';

const getStatusColor = (status: string): 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info' => {
  const colors: Record<string, any> = {
    'DRAFT': 'default', 'CONFIRMED': 'info', 'PROCESSING': 'primary',
    'SHIPPED': 'warning', 'DELIVERED': 'success', 'CANCELLED': 'error'
  };
  return colors[status?.toUpperCase()] || 'default';
};

const SalesOrderList: React.FC = () => {
  const [orders, setOrders] = useState<SalesOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { t } = useLanguage();

  useEffect(() => { loadOrders(); }, []);

  const loadOrders = async () => {
    try { const data = await salesService.getAllOrders(); setOrders(data); }
    catch (error) { console.error('Error loading orders:', error); }
    finally { setLoading(false); }
  };

  const handleGenerateInvoice = async (id: number, orderNumber: string) => {
    if (!window.confirm(`Generate invoice from ${orderNumber}?`)) return;
    try {
      const result = await salesService.generateInvoiceFromSO(id);
      alert(result.message);
      navigate('/invoices');
    } catch (error: any) {
      alert(error.response?.data?.message || 'Error generating invoice');
    }
  };

  const columns: GridColDef[] = [
    { field: 'orderNumber', headerName: t('invoices.invoice_number'), width: 200 },
    {
      field: 'orderDate', headerName: t('common.date'), width: 120,
      renderCell: (params: GridRenderCellParams) => new Date(params.value).toLocaleDateString()
    },
    { field: 'customerName', headerName: t('sales_orders.customer'), flex: 1 },
    {
      field: 'status', headerName: t('common.status'), width: 130,
      renderCell: (params: GridRenderCellParams) => (
        <Chip label={params.value} color={getStatusColor(params.value as string)} size="small" />
      )
    },
    {
      field: 'totalAmount', headerName: t('common.total'), width: 130, align: 'right', headerAlign: 'right',
      renderCell: (params: GridRenderCellParams) => {
        const curr = params.row.currencyCode || 'USD';
        return `${Number(params.value).toFixed(2)} ${curr}`;
      }
    },
    {
      field: 'actions', headerName: t('common.actions'), width: 100, sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const canInvoice = ['CONFIRMED', 'PROCESSING', 'SHIPPED', 'DELIVERED'].includes(params.row.status);
        return canInvoice ? (
          <Tooltip title={t('sales_orders.generate_invoice')}>
            <IconButton size="small" color="primary"
              onClick={() => handleGenerateInvoice(params.row.id, params.row.orderNumber)}>
              <Receipt fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null;
      }
    }
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">{t('sales_orders.title')}</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/sales-orders/new')}>
          {t('sales_orders.new')}
        </Button>
      </Box>
      <DataGrid rows={orders} columns={columns} loading={loading} autoHeight
        pageSizeOptions={[10, 25, 50]}
        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        disableRowSelectionOnClick />
    </Box>
  );
};

export default SalesOrderList;
